import * as React from 'react';
import { GraphqlQuery } from 'requestapijs';
import { Loading, Table, Input, Card, Row, Col, Button, notify, Page } from 'sha-el-design';
import { Colums } from 'sha-el-design/lib/components/Table/Table';
import { QuestionnaireNodeConnection, QuestionnaireNodeEdge, QuestionnaireNode } from '../../../../schema';
import { Link } from 'react-router-dom';
import { Pagination } from '../../Common/Pagination';
import { Inject } from '../../../helpers';
import { questionnaireListQuery } from './constant';
import { nestedAccess } from 'sha-el-design/lib/helpers';
import { QuestionnaireService } from '../../../service/questionnaireService';
import { FaEdit } from 'react-icons/fa';
import { MdHome, MdArrowBack } from 'react-icons/md';
import '../../Common/listPageStyles';

export class QuestionnaireList extends React.Component<{}, State> {
  @Inject questionnaireService: QuestionnaireService;

  constructor(props) {
    super(props);

    this.state = {
      after: '',
      name: '',
      state: '',
      searchName: '',
      searchState: '',
      updateCount: 0,
      retryCount: 0,
      busy: {},
    };
  }

  // Debounced; `name`/`state` are what the inputs show, `searchName`/
  // `searchState` are what the query sees 300ms later. Searching also resets
  // the `after` cursor -- the old inputs kept the page-N cursor, so a search
  // from page 2+ started mid-way through the filtered set.
  private searchTimer: number = null;

  onSearch = (field: 'name' | 'state', value: string) => {
    this.setState({ [field]: value } as any);
    window.clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(
      () => this.setState({
        searchName: this.state.name,
        searchState: this.state.state,
        after: '',
      }),
      300,
    );
  }

  componentWillUnmount() {
    window.clearTimeout(this.searchTimer);
  }

  // The toggle re-sends the row through the full-replacement mutation, so the
  // payload is built explicitly from every model field the list query fetches.
  // An earlier version spread the node and omitted the statements (the list
  // query didn't fetch them), which would have blanked the hand-written call
  // scripts on the first toggle.
  toggleActive = (questionnaire: QuestionnaireNode) => {
    this.setState({ busy: { ...this.state.busy, [questionnaire.id]: true } });
    const done = (extra: Partial<State> = {}) => this.setState({
      busy: { ...this.state.busy, [questionnaire.id]: false },
      ...extra as any,
    });
    this.questionnaireService.add(
      {
        id: questionnaire.id,
        name: questionnaire.name,
        state: nestedAccess(questionnaire.state, 'id'),
        town: nestedAccess(questionnaire.town, 'id'),
        questions: questionnaire.questions,
        openingStatement: questionnaire.openingStatement,
        closingStatement: questionnaire.closingStatement,
        active: !questionnaire.active,
      } as any,
      (response) => {
        // State is released immediately in every branch -- notify's callBack
        // only fires when the toast CLOSES, which left the button stuck on
        // 'Saving...' for the whole toast duration.
        if (!response.data.createQuestionnaire && response.errors) {
          done();
          return notify({
            type: 'error',
            title: response.errors[0].message,
            duration: 3500,
          });
        }
        const errors = response.data.createQuestionnaire.errors;
        if (errors && errors.length) {
          done();
          // The serializer's guards (one active per state; no edits while the
          // state has open work) arrive as nonFieldErrors -- show the message,
          // not a shouted internal field name.
          const field = errors[0].field;
          return notify({
            type: 'error',
            title: (field === 'nonFieldErrors' || field === 'non_field_errors')
              ? 'Cannot change status' : field.toUpperCase(),
            message: errors[0].messages[0],
            duration: 4500,
          });
        }
        done({ updateCount: this.state.updateCount + 1 } as any);
        notify({
          type: 'success',
          title: `Questionnaire ${questionnaire.active ? 'deactivated' : 'activated'}`,
          duration: 2500,
        });
      },
      // Transport failure used to be swallowed by a literal `(e) => e`,
      // leaving the row button stuck on 'Saving...' forever.
      () => {
        done();
        notify({
          type: 'error',
          title: 'Could not reach the server',
          message: 'The status was not changed.',
          duration: 4500,
        });
      },
    );
  }

  columns = (): Array<Colums<QuestionnaireNodeEdge>> => [{
    key: 'questionnaire',
    header: 'Questionnaire',
    render: (text, obj) => (
      <div>
        <Link className='cs-q__name' to={`/dashboard/questionnaire/${obj.node.id}`}>
          {obj.node.name}
        </Link>
        {obj.node.openingStatement ? (
          <p className='cs-q__desc'>
            {obj.node.openingStatement.slice(0, 70)}
            {obj.node.openingStatement.length > 70 ? '…' : ''}
          </p>
        ) : (
          <p className='cs-q__desc'>No opening statement</p>
        )}
      </div>
    ),
  }, {
    key: 'state',
    header: 'State',
    render: (text, obj) => nestedAccess(obj.node.state, 'name') || '—',
  }, {
    key: 'questions',
    header: 'Questions',
    render: (text, obj) => {
      const n = (obj.node.questions || []).length;
      return <span className='cs-list__count'>{n} question{n === 1 ? '' : 's'}</span>;
    },
  }, {
    key: 'status',
    header: 'Status',
    render: (text, obj) => (
      obj.node.active
        ? <span className='cs-q__status cs-q__status--free'>Active</span>
        : <span className='cs-q__status cs-q__status--off'>Inactive</span>
    ),
  }, {
    key: 'toggle',
    header: '',
    render: (text, obj) => (
      <button
        type='button'
        className={`cs-q__toggle ${obj.node.active ? 'cs-q__toggle--off' : 'cs-q__toggle--on'}`}
        disabled={!!this.state.busy[obj.node.id]}
        onClick={() => this.toggleActive(obj.node)}
      >
        {this.state.busy[obj.node.id] ? 'Saving...' : obj.node.active ? 'Deactivate' : 'Activate'}
      </button>
    ),
  }, {
    key: 'edit',
    header: '',
    render: (text, obj) => (
      <Link
        className='cs-list__call'
        to={`/dashboard/questionnaire/${obj.node.id}`}
        title='Edit'
      >
        <FaEdit />
      </Link>
    ),
  }]

  breadcrumbs = () => ({
    seperator: '/',
    paths: [
      { text: <MdHome />, link: '/dashboard' },
      { text: 'Questionnaire', link: '/dashboard/questionnaire' },
    ].map((v) => ({
      render: () => (
        <Link key={v.link} to={v.link}>
          {v.text}
        </Link>
      ),
    })),
  })

  render() {
    const { searchName, searchState } = this.state;
    return (
      <div className='cs-list'>
        <Page
          title={'Questionnaire List'}
          breadcrumbs={this.breadcrumbs()}
          backIcon={
            <Link to={'/dashboard'}>
              <MdArrowBack />
            </Link>
          }
          extra={
            <Row>
              <Col offset={12} span={10}>
                <span className='cs-list__action'>
                  <Link to='/dashboard/questionnaire/add'>
                    <Button style={{ width: '100%' }} type='primary'>Add Questionnaire</Button>
                  </Link>
                </span>
              </Col>
            </Row>
          }
        >
          <Card>
            {[
              <div className='cs-list__filters' key='filters'>
                <div className='cs-list__filter'>
                  <Input
                    placeholder='Search by name'
                    value={this.state.name}
                    onChange={(e) => this.onSearch('name', e.target.value)}
                  />
                </div>
                <div className='cs-list__filter'>
                  <Input
                    placeholder='Filter by state'
                    value={this.state.state}
                    onChange={(e) => this.onSearch('state', e.target.value)}
                  />
                </div>
              </div>,
              <GraphqlQuery
                key={`query-${this.state.retryCount}`}
                queryString={questionnaireListQuery}
                variables={{
                  after: this.state.after,
                  name: searchName,
                  state: searchState,
                  refresh: this.state.updateCount,
                }}
                render={(response: { allQuestionnaires: QuestionnaireNodeConnection }, error, loading) => {
                  if (error) {
                    // The query component latches its error state, so one
                    // failed refetch used to leave 'Something went wrong'
                    // on screen until the route remounted. Bumping
                    // `retryCount` changes the variables, which remounts the
                    // query and clears the latch.
                    return (
                      <div className='cs-list__empty'>
                        <h4>Could not load questionnaires</h4>
                        <p>The server did not respond. Nothing has been changed.</p>
                        <button
                          type='button'
                          className='cs-list__page-btn'
                          style={{ marginTop: '12px' }}
                          onClick={() => this.setState({ retryCount: this.state.retryCount + 1 })}
                        >
                          Try again
                        </button>
                      </div>
                    );
                  }
                  return (
                    <Loading
                      isLoading={loading}
                      render={() => (
                        <div>
                          {response.allQuestionnaires.edges.length ? (
                            <Table
                              data={response.allQuestionnaires.edges}
                              columns={this.columns()}
                            />
                          ) : (
                            <div className='cs-list__empty'>
                              <h4>
                                {searchName || searchState
                                  ? 'No matching questionnaires'
                                  : 'No questionnaires yet'}
                              </h4>
                              <p>
                                {searchName || searchState
                                  ? 'Nothing matches the current filters.'
                                  : 'Use Add Questionnaire to create the first one.'}
                              </p>
                            </div>
                          )}
                          <Pagination
                            key={`${searchName}|${searchState}`}
                            endCursor={response.allQuestionnaires.pageInfo.endCursor}
                            hasNextPage={response.allQuestionnaires.pageInfo.hasNextPage}
                            after={(after) => this.setState({ after })}
                          />
                        </div>
                      )}
                    />
                  );
                }}
              />,
            ]}
          </Card>
        </Page>
      </div>
    );
  }
}

interface State {
  after: string;
  name: string;
  state: string;
  searchName: string;
  searchState: string;
  updateCount: number;
  retryCount: number;
  busy: {
    [id: string]: boolean;
  };
}
